import { jsx } from '@amazon/vinyl-tsx'
import { createDisposer } from '@amazon/vinyl-util'
import { Icon } from '@/components/Icon'
import { documentEvents, windowEvents } from '@/util/interaction'
import { onNavigate } from '@/router/router'

export interface ContextMenuItem {
    readonly label: string
    readonly icon?: string
    readonly run: () => void
    readonly disabled?: boolean
    readonly destructive?: boolean
}

export interface OpenContextMenuOptions {
    readonly items: readonly ContextMenuItem[]
    /**
     * Anchor position for the menu — typically `event.clientX/Y` from
     * the originating contextmenu event. The helper clamps the menu
     * inside the viewport so a click near the right / bottom edge
     * doesn't render off-screen.
     */
    readonly x: number
    readonly y: number
    /**
     * Optional title shown at the top of the menu (small caps), useful
     * to disambiguate which entity the menu is operating on.
     */
    readonly title?: string
}

/**
 * Lightweight at-cursor context menu — mirrors a native right-click
 * popup. Renders as a floating panel at (x, y), clamped into the
 * viewport. Closes on:
 *  - any click anywhere
 *  - Escape
 *  - route change
 *  - the next contextmenu event (so the user can right-click another
 *    target without two menus stacking)
 *
 * Distinct from the modal layer — the menu is positionally anchored
 * and doesn't dim the page. Use this for surface-level actions on a
 * card / row; reserve the modal layer for confirmations and full
 * sheets.
 */
export function openContextMenu(options: OpenContextMenuOptions): void {
    // Destroy any pre-existing menu so right-clicking another card
    // replaces the popup rather than stacking.
    closeAnyContextMenu()

    const { add, dispose } = createDisposer()
    const menu: HTMLDivElement = (
        <div
            className="contextMenu"
            role="menu"
            tabIndex={-1}
            onclick={(event) => {
                event.stopPropagation()
            }}
            oncontextmenu={(event) => {
                event.preventDefault()
            }}
        >
            {options.title ? <div className="contextMenuTitle">{options.title}</div> : null}
            <ul className="contextMenuList">
                {...options.items.map((item) => (
                    <li>
                        <button
                            className="contextMenuItem"
                            classList={[item.destructive ? 'destructive' : null]}
                            disabled={item.disabled}
                            onclick={() => {
                                if (item.disabled) return
                                close()
                                item.run()
                            }}
                            role="menuitem"
                        >
                            {item.icon ? <Icon icon={item.icon as never} /> : null}
                            <span className="contextMenuLabel">{item.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    ) as HTMLDivElement

    menu.style.position = 'fixed'
    menu.style.left = `${options.x}px`
    menu.style.top = `${options.y}px`
    // Make the menu visible off-screen first so we can measure its
    // size, then clamp into the viewport.
    menu.style.visibility = 'hidden'

    document.body.appendChild(menu)

    // Clamp inside viewport. Read after append so the browser has
    // computed the menu's natural size.
    const rect = menu.getBoundingClientRect()
    const margin = 8
    let nextX = options.x
    let nextY = options.y
    if (rect.right > window.innerWidth - margin) {
        nextX = Math.max(margin, window.innerWidth - rect.width - margin)
    }
    if (rect.bottom > window.innerHeight - margin) {
        nextY = Math.max(margin, window.innerHeight - rect.height - margin)
    }
    menu.style.left = `${nextX}px`
    menu.style.top = `${nextY}px`
    menu.style.visibility = ''

    // Focus the first item for keyboard users — Tab cycles through.
    const firstItem = menu.querySelector<HTMLElement>('button:not([disabled])')
    firstItem?.focus()

    function close() {
        if (currentMenu !== menu) return
        currentMenu = null
        dispose()
        menu.remove()
    }

    // Tear down when any of the close-triggering signals fire. The
    // listener attachment is deferred so the contextmenu event that
    // opened the menu finishes bubbling first — otherwise the
    // mousedown / contextmenu close-on-outside listeners catch the
    // original event and tear down the menu before the user sees it.
    // Close-listener attachment is deferred until the next macrotask
    // so the contextmenu event AND its trailing mouseup finish
    // bubbling first. queueMicrotask is too eager — Chromium fires
    // the mouseup *after* contextmenu in the same task cycle, so a
    // microtask schedules the close-on-mousedown listener while the
    // mouseup is still in flight; an inner button receiving the
    // right-click then takes the focus blur path that hits our blur
    // listener and immediately tears the menu down.
    setTimeout(() => {
        if (currentMenu !== menu) return
        add(
            documentEvents.on('mousedown', (event) => {
                if (!menu.contains(event.target as Node)) close()
            })
        )
        add(
            windowEvents.on('keydown', (event) => {
                if (event.key === 'Escape') {
                    event.preventDefault()
                    close()
                }
            })
        )
        add(
            documentEvents.on('contextmenu', (event) => {
                // Allow the new contextmenu event to open a fresh menu;
                // close this one first so the new menu's
                // closeAnyContextMenu() call doesn't race.
                if (!menu.contains(event.target as Node)) close()
            })
        )
        add(onNavigate(() => close()))
        add(windowEvents.on('resize', () => close()))
        // Intentionally NOT closing on `blur` — right-clicking inside
        // a button blurs that button (focus jumps to the menu we just
        // opened) and the bare blur listener tore us right back down.
    }, 0)

    currentMenu = menu
}

let currentMenu: HTMLDivElement | null = null

export function closeAnyContextMenu(): void {
    if (!currentMenu) return
    currentMenu.dispatchEvent(new Event('close'))
    currentMenu.remove()
    currentMenu = null
}
